import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import {
  notificationChannels,
  WaitingWorkSchema,
  WaitingWorkSliceSchema,
  type WaitingWork,
  type WaitingWorkGroup,
  type WaitingWorkSlice,
} from "@thalamos/haven-common";

import { useApiAxios, type ErrorBody } from "../axios/api.js";

const parseWaitingWork = parseAxiosResponse(WaitingWorkSchema);

export const config = {
  waitingWork: {
    list: () => ({
      url: "/api/users/me/waiting-work",
      method: "get",
      transformResponse: parseWaitingWork,
    }),
    rows: (asked: { group: WaitingWorkGroup; limit: number }) => ({
      url: "/api/users/me/waiting-work/rows",
      method: "get",
      params: asked,
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => ({
  waitingWork: {
    /**
     * A group's first `limit` rows, or `undefined` where the server refused
     * them. Rejects when the request fails or its body is not a slice.
     */
    rows: async (asked: {
      group: WaitingWorkGroup;
      limit: number;
    }): Promise<WaitingWorkSlice | undefined> => {
      const response = await apiAxios.request<unknown>(
        config.waitingWork.rows(asked),
      );

      return response.status === 200
        ? WaitingWorkSliceSchema.parse(response.data)
        : undefined;
    },
  },
});

export const apiHooks = () => ({
  waitingWork: {
    /** What is waiting on the signed-in user, read only when asked. */
    list: function useWaitingWorkList() {
      return useApiAxios<WaitingWork | ErrorBody>(config.waitingWork.list(), {
        manual: true,
      });
    },
  },
});

/**
 * The channels of the young people the rows of `slices` name, so acting on one
 * elsewhere can take its row away. Something newly waiting arrives on the next
 * listing.
 */
export const waitingWorkChannels = (
  slices: readonly WaitingWorkSlice[],
): readonly string[] =>
  slices.flatMap((slice) =>
    slice.rows.map((row) => notificationChannels.person(row.personId)),
  );
